/*
 * Branded share card composer — ON THE DEVICE ONLY (canon draft 11b;
 * REFERRAL-BUILD-SPLIT-V1 §3 item 5). Pure canvas drawing + export. Nothing
 * here makes a network request with the photo, the composed image or the
 * message: images are read from the device (blob: URL) or loaded as <img>
 * with crossOrigin="anonymous"; the output is a local File for the share sheet.
 *
 * Draw order (every rectangle from the template's layout; fonts are system
 * stacks so drawing never waits on a web font):
 *   1. background: the photo (else the event hero) cropped to COVER with
 *      zoom 1.0–2.5 + pan; with neither, a subtle panel_bg→accent gradient;
 *   2. a top scrim (image backgrounds only), never above the Stories UI band;
 *   3. top zone: logo (on-dark variant directly, else on a light plate) +
 *      event name + date — or, without a logo, the event name as a large
 *      WORDMARK in panel_text;
 *   4. the solid panel_bg panel: the message (max_lines, ellipsis), the
 *      EXCLUSIVE code zone (accent frame + code_label + code, nothing else),
 *      and the short link.
 *
 * Export: toBlob('image/png'). If the canvas is tainted (an image host without
 * CORS slipped through) toBlob throws; the card is redrawn WITHOUT the remote
 * images (wordmark + colour path) and exported again, so a tainted canvas is
 * never exported. Still failing ⇒ null (the caller shares text + link).
 */
import type { Rect, ShareCardLayout, ShareCardSize, ShareCardTemplateV1 } from './shareCardTemplate'

export const ZOOM_MIN = 1
export const ZOOM_MAX = 2.5

export interface Crop { zoom: number; panX: number; panY: number }
export const DEFAULT_CROP: Crop = { zoom: 1, panX: 0, panY: 0 }

export const clampZoom = (z: number): number => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number.isFinite(z) ? z : ZOOM_MIN))
export const clampPan = (p: number): number => Math.min(0.5, Math.max(-0.5, Number.isFinite(p) ? p : 0))

/** Anything drawable with a known intrinsic size. */
export interface CardImage { source: CanvasImageSource; width: number; height: number }

export interface CardImages {
  /** The buyer's photo (device-local). Wins over the hero. */
  photo: CardImage | null
  /** settings' hero_image_url, when it loaded with CORS. */
  hero: CardImage | null
  /** logo_on_dark_url (onDark) or logo_url (on a light plate), when it loaded. */
  logo: (CardImage & { onDark: boolean }) | null
}

export const NO_IMAGES: CardImages = { photo: null, hero: null, logo: null }

const SANS = 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif'
const MONO = 'ui-monospace, Menlo, Consolas, "Courier New", monospace'

/** Which path drew the top zone / background — exposed for tests and logs. */
export interface DrawReport { background: 'photo' | 'hero' | 'gradient'; top: 'logo' | 'wordmark' }

function setFont(ctx: CanvasRenderingContext2D, weight: number, size: number, family: string) {
  ctx.font = `${weight} ${Math.round(size)}px ${family}`
}

/** Shrink `size` until `text` fits `maxW` (one line). */
function fitSize(ctx: CanvasRenderingContext2D, text: string, weight: number, start: number, min: number, maxW: number, family: string): number {
  let size = start
  for (;;) {
    setFont(ctx, weight, size, family)
    if (ctx.measureText(text).width <= maxW || size <= min) return size
    size = Math.max(min, size - 2)
  }
}

function ellipsize(ctx: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (ctx.measureText(text).width <= maxW) return text
  let t = text
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxW) t = t.slice(0, -1)
  return `${t.trimEnd()}…`
}

/** Greedy word wrap to at most `maxLines`; the last line gets an ellipsis when cut. */
export function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxW: number, maxLines: number): string[] {
  const words = text.split(/\s+/).filter(Boolean)
  const lines: string[] = []
  let line = ''
  let i = 0
  for (; i < words.length; i++) {
    const next = line ? `${line} ${words[i]}` : words[i]
    if (ctx.measureText(next).width <= maxW || !line) {
      line = next
      continue
    }
    lines.push(line)
    line = words[i]
    if (lines.length === maxLines) break
  }
  if (lines.length < maxLines && line) {
    lines.push(line)
    i = words.length
  }
  const cut = i < words.length
  return lines.slice(0, maxLines).map((l, idx, arr) => (idx === arr.length - 1 && cut ? ellipsize(ctx, `${l}…`, maxW) : ellipsize(ctx, l, maxW)))
}

function clipTo(ctx: CanvasRenderingContext2D, r: Rect, draw: () => void) {
  ctx.save()
  ctx.beginPath()
  ctx.rect(r.x, r.y, r.w, r.h)
  ctx.clip()
  draw()
  ctx.restore()
}

/** Cover-fit with zoom and pan: the image ALWAYS covers the canvas. */
export function coverRect(imgW: number, imgH: number, W: number, H: number, crop: Crop): Rect {
  const s = Math.max(W / imgW, H / imgH) * clampZoom(crop.zoom)
  const w = imgW * s
  const h = imgH * s
  const ox = w - W
  const oy = h - H
  return { x: -ox / 2 + clampPan(crop.panX) * ox, y: -oy / 2 + clampPan(crop.panY) * oy, w, h }
}

function drawBackground(ctx: CanvasRenderingContext2D, L: ShareCardLayout, t: ShareCardTemplateV1, images: CardImages, crop: Crop): DrawReport['background'] {
  const bg = images.photo ?? images.hero
  if (bg) {
    const r = coverRect(bg.width, bg.height, L.w, L.h, crop)
    ctx.drawImage(bg.source, r.x, r.y, r.w, r.h)
    // Top scrim keeps the logo / name legible; never above the Stories UI band.
    const from = L.platform_ui_top ?? 0
    const to = L.top.y + L.top.h + 60
    const g = ctx.createLinearGradient(0, from, 0, to)
    g.addColorStop(0, 'rgba(0,0,0,0.55)')
    g.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, from, L.w, to - from)
    return images.photo ? 'photo' : 'hero'
  }
  ctx.fillStyle = t.colours.panel_bg
  ctx.fillRect(0, 0, L.w, L.h)
  const g = ctx.createLinearGradient(0, 0, L.w, L.h)
  g.addColorStop(0, t.colours.panel_bg)
  g.addColorStop(1, t.colours.accent)
  ctx.globalAlpha = 0.35
  ctx.fillStyle = g
  ctx.fillRect(0, 0, L.w, L.h)
  ctx.globalAlpha = 1
  return 'gradient'
}

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

function drawTop(ctx: CanvasRenderingContext2D, L: ShareCardLayout, t: ShareCardTemplateV1, images: CardImages, onImage: boolean): DrawReport['top'] {
  const T = L.top
  const ink = onImage ? '#ffffff' : t.colours.panel_text
  const date = [t.event_date_display, t.venue_display].filter(Boolean).join(' · ')
  const dateSize = Math.min(36, T.h * 0.13)
  const dateBlock = date ? dateSize * 1.5 : 0
  ctx.textBaseline = 'alphabetic'
  ctx.textAlign = 'left'
  let report: DrawReport['top']
  clipTo(ctx, T, () => {
    if (images.logo) {
      const lg = images.logo
      const pad = lg.onDark ? 0 : 16
      const maxH = Math.min(110, T.h * 0.4)
      const scale = Math.min(maxH / lg.height, (T.w * 0.5) / lg.width)
      const lw = lg.width * scale
      const lh = lg.height * scale
      if (!lg.onDark) {
        ctx.fillStyle = 'rgba(255,255,255,0.94)'
        roundRectPath(ctx, T.x, T.y, lw + pad * 2, lh + pad * 2, 18)
        ctx.fill()
      }
      ctx.drawImage(lg.source, T.x + pad, T.y + pad, lw, lh)
      const nameTop = T.y + lh + pad * 2 + 14
      const nameSize = fitSize(ctx, t.event_name, 800, Math.min(58, T.h - (nameTop - T.y) - dateBlock), 24, T.w, SANS)
      ctx.fillStyle = ink
      ctx.fillText(ellipsize(ctx, t.event_name, T.w), T.x, nameTop + nameSize)
      if (date) {
        setFont(ctx, 600, dateSize, SANS)
        ctx.fillStyle = ink
        ctx.fillText(ellipsize(ctx, date, T.w), T.x, nameTop + nameSize + dateSize * 1.35)
      }
      report = 'logo'
      return
    }
    // WORDMARK: the event name, large, in place of the logo.
    let size = Math.min(120, (T.h - dateBlock) * 0.5)
    let lines: string[] = []
    for (;;) {
      setFont(ctx, 900, size, SANS)
      lines = wrapLines(ctx, t.event_name, T.w, 2)
      const fits = lines.length * size * 1.08 <= T.h - dateBlock
      if ((fits && lines.every((l) => !l.endsWith('…'))) || size <= 36) break
      size -= 4
    }
    ctx.fillStyle = ink
    lines.forEach((l, i) => ctx.fillText(l, T.x, T.y + size + i * size * 1.08))
    if (date) {
      setFont(ctx, 600, dateSize, SANS)
      ctx.fillStyle = ink
      ctx.fillText(ellipsize(ctx, date, T.w), T.x, T.y + lines.length * size * 1.08 + dateSize * 1.2)
    }
    report = 'wordmark'
  })
  return report!
}

function drawPanel(ctx: CanvasRenderingContext2D, L: ShareCardLayout, t: ShareCardTemplateV1, message: string) {
  const { panel_bg, panel_text, accent } = t.colours
  ctx.globalAlpha = 1
  ctx.fillStyle = panel_bg
  ctx.fillRect(0, L.panel.y, L.w, L.panel.h)
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'

  // Message (the buyer's own words; max_lines, ellipsis).
  const M = L.message
  const msg = message.trim()
  if (msg) {
    const size = Math.min(42, (M.h / M.max_lines) * 0.8)
    clipTo(ctx, M, () => {
      setFont(ctx, 600, size, SANS)
      ctx.fillStyle = panel_text
      wrapLines(ctx, msg, M.w, M.max_lines).forEach((l, i) => ctx.fillText(l, M.x, M.y + size + i * size * 1.22))
    })
  }

  // EXCLUSIVE code zone: frame + label + code only.
  const C = L.code
  clipTo(ctx, C, () => {
    ctx.strokeStyle = accent
    ctx.lineWidth = 4
    roundRectPath(ctx, C.x + 2, C.y + 2, C.w - 4, C.h - 4, 16)
    ctx.stroke()
    const labelSize = Math.min(34, C.h * 0.22)
    setFont(ctx, 800, labelSize, SANS)
    ctx.fillStyle = accent
    ctx.fillText(ellipsize(ctx, t.code_label, C.w - 48), C.x + 24, C.y + 14 + labelSize)
    const codeMax = C.h - labelSize - 44
    fitSize(ctx, t.code, 700, codeMax, 24, C.w - 48, MONO) // leaves ctx.font set
    ctx.fillStyle = panel_text
    ctx.fillText(t.code, C.x + 24, C.y + C.h - 20)
  })

  // Short link.
  const K = L.link
  clipTo(ctx, K, () => {
    // Shrink before cutting: the link should stay whole wherever it can.
    fitSize(ctx, t.short_link, 500, Math.min(32, K.h * 0.7), 20, K.w, SANS)
    ctx.fillStyle = panel_text
    ctx.globalAlpha = 0.88
    ctx.fillText(ellipsize(ctx, t.short_link, K.w), K.x, K.y + K.h * 0.72)
    ctx.globalAlpha = 1
  })
}

/** Draw one card into `ctx` (already sized L.w × L.h). */
export function drawShareCard(
  ctx: CanvasRenderingContext2D,
  t: ShareCardTemplateV1,
  size: ShareCardSize,
  message: string,
  images: CardImages,
  crop: Crop,
): DrawReport {
  const L = t.layouts[size]
  ctx.clearRect(0, 0, L.w, L.h)
  const background = drawBackground(ctx, L, t, images, crop)
  const top = drawTop(ctx, L, t, images, background !== 'gradient')
  drawPanel(ctx, L, t, message)
  return { background, top }
}

function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b), 'image/png')
    } catch {
      // SecurityError: tainted canvas.
      resolve(null)
    }
  })
}

export interface ComposeResult {
  file: File | null
  report: DrawReport | null
  /** true ⇒ the remote images were dropped to get an exportable canvas. */
  degraded: boolean
}

/** Compose `size` off-screen and export a PNG File. Falls back to the
 *  wordmark + colour path (no remote images), then to no photo, before
 *  giving up (file null ⇒ the caller shares text + link). */
export async function composeShareCardFile(
  t: ShareCardTemplateV1,
  size: ShareCardSize,
  message: string,
  images: CardImages,
  crop: Crop,
  createCanvas: () => HTMLCanvasElement = () => document.createElement('canvas'),
): Promise<ComposeResult> {
  const L = t.layouts[size]
  const attempts: CardImages[] = [images]
  if (images.hero || images.logo) attempts.push({ photo: images.photo, hero: null, logo: null })
  if (images.photo) attempts.push(NO_IMAGES)
  for (let i = 0; i < attempts.length; i++) {
    const canvas = createCanvas()
    canvas.width = L.w
    canvas.height = L.h
    const ctx = canvas.getContext('2d')
    if (!ctx) return { file: null, report: null, degraded: false }
    const report = drawShareCard(ctx, t, size, message, attempts[i], crop)
    const blob = await canvasToPng(canvas)
    if (blob) {
      const file = new File([blob], `${t.code}-${size === 'square' ? '1080x1080' : '1080x1920'}.png`, { type: 'image/png' })
      return { file, report, degraded: i > 0 }
    }
  }
  return { file: null, report: null, degraded: true }
}

/** Load a REMOTE template image with crossOrigin="anonymous" (so a CORS-less
 *  host fails to load instead of tainting the canvas). Resolves null on error
 *  or timeout — the card is then drawn without that image. */
export function loadRemoteImage(src: string | null, timeoutMs = 8000): Promise<CardImage | null> {
  if (!src) return Promise.resolve(null)
  return new Promise((resolve) => {
    let done = false
    const finish = (v: CardImage | null) => {
      if (done) return
      done = true
      clearTimeout(timer)
      resolve(v)
    }
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.decoding = 'async'
    img.onload = () => {
      const w = img.naturalWidth || img.width
      const h = img.naturalHeight || img.height
      finish(w > 0 && h > 0 ? { source: img, width: w, height: h } : null)
    }
    img.onerror = () => finish(null)
    const timer = setTimeout(() => finish(null), timeoutMs)
    img.src = src
  })
}

/** Load the buyer's photo from the DEVICE (a blob: URL from
 *  URL.createObjectURL — same-origin, never uploaded). */
export function loadLocalImage(objectUrl: string): Promise<CardImage | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const w = img.naturalWidth || img.width
      const h = img.naturalHeight || img.height
      resolve(w > 0 && h > 0 ? { source: img, width: w, height: h } : null)
    }
    img.onerror = () => resolve(null)
    img.src = objectUrl
  })
}
